import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, useAuth } from "../App";
import { Calendar, CheckCircle2, IndianRupee, Stethoscope, FileText, X, ArrowRight, Clock, User as UserIcon } from "lucide-react";

export default function VetVisits() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [completing, setCompleting] = useState(null);

  const load = () => axios.get(`${API}/partner/vet/today-appointments`).then(r => setData(r.data)).catch(() => setData("err"));
  useEffect(() => { load(); }, []);

  if (!user) return null;
  if (data === null) return <div className="text-ink-soft">Loading…</div>;
  if (data === "err") return <div className="text-ink-soft">No vet profile yet. Visit <Link to="/app/partner" className="text-brand font-bold">Settings</Link> to set up your clinic.</div>;

  const today = new Date().toDateString();
  const upcoming = (data.items || []).filter(a => a.status === "upcoming");
  const completed = (data.items || []).filter(a => a.status === "completed");
  const todays = upcoming.filter(a => new Date(a.date_time).toDateString() === today);
  const later = upcoming.filter(a => new Date(a.date_time).toDateString() !== today);

  return (
    <div className="space-y-6" data-testid="vet-visits-page">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold inline-flex items-center gap-1"><Stethoscope className="w-4 h-4"/>Today at your clinic</div>
        <h1 className="font-heading text-3xl font-extrabold mt-1">Visits</h1>
        <p className="text-ink-soft text-sm">Complete a visit to write the prescription, mark it done, and earn ₹{data.vet_fee} (your default fee).</p>
      </header>

      <Section title={`Today (${todays.length})`} icon={Clock}>
        {todays.length === 0 ? <Empty msg="No visits scheduled for today."/> :
          todays.map(a => <Row key={a.appointment_id} a={a} fee={data.vet_fee} onComplete={() => setCompleting(a)} primary/>)}
      </Section>

      <Section title={`Upcoming (${later.length})`} icon={Calendar}>
        {later.length === 0 ? <Empty msg="No upcoming visits."/> :
          later.map(a => <Row key={a.appointment_id} a={a} fee={data.vet_fee} onComplete={() => setCompleting(a)}/>)}
      </Section>

      <Section title={`Recently completed (${completed.length})`} icon={CheckCircle2}>
        {completed.length === 0 ? <Empty msg="No completed visits yet."/> :
          completed.slice(0, 10).map(a => <Row key={a.appointment_id} a={a} done/>)}
      </Section>

      {completing && <CompleteVisitModal appt={completing} defaultFee={data.vet_fee} onClose={() => { setCompleting(null); load(); }}/>}
    </div>
  );
}

function Section({ title, icon: I, children }) {
  return (
    <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft" data-testid={`vet-section-${title.split(' ')[0].toLowerCase()}`}>
      <h2 className="font-heading text-lg font-bold inline-flex items-center gap-2 mb-3"><I className="w-4 h-4 text-brand"/>{title}</h2>
      <div className="divide-y divide-line">{children}</div>
    </section>
  );
}

function Row({ a, fee, onComplete, primary, done }) {
  const dt = new Date(a.date_time);
  return (
    <div data-testid={`vet-row-${a.appointment_id}`} className="py-3 flex items-center justify-between gap-3 flex-wrap text-sm">
      <div className="flex-1 min-w-[180px]">
        <div className="font-heading font-extrabold">{a.pet_name} <span className="text-ink-soft font-normal text-sm">· {a.service}</span></div>
        <div className="text-xs text-ink-soft inline-flex items-center gap-1.5 mt-0.5"><UserIcon className="w-3 h-3"/>{a.owner_name || "—"} <span className="opacity-50">·</span> <Calendar className="w-3 h-3"/>{dt.toLocaleDateString()} {dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
      </div>
      {done ? (
        <>
          <span className="text-xs font-heading font-extrabold text-brand-green">₹{a.amount_inr || fee}</span>
          {a.latest_prescription_id ? (
            <Link to={`/app/prescription/${a.pet_id}/${a.latest_prescription_id}`} data-testid={`view-rx-${a.appointment_id}`} className="text-xs font-bold text-brand inline-flex items-center gap-1 hover:underline">View Rx <ArrowRight className="w-3 h-3"/></Link>
          ) : (
            <Link to={`/app/vet/prescribe/${a.pet_id}?appt_id=${a.appointment_id}`} data-testid={`write-rx-${a.appointment_id}`} className="text-xs font-bold text-ink-soft inline-flex items-center gap-1 hover:underline">Write Rx <ArrowRight className="w-3 h-3"/></Link>
          )}
        </>
      ) : (
        <>
          <Link to={`/app/vet/prescribe/${a.pet_id}?appt_id=${a.appointment_id}`} data-testid={`write-rx-${a.appointment_id}`} className="bg-surface-muted rounded-full px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 hover:bg-brand hover:text-white transition-colors">
            <FileText className="w-3 h-3"/> Write Rx
          </Link>
          <button onClick={onComplete} data-testid={`complete-${a.appointment_id}`} className={`rounded-full px-4 py-1.5 text-xs font-bold inline-flex items-center gap-1 ${primary ? "bg-brand text-white hover:bg-brand-hover" : "bg-ink text-white hover:bg-brand"}`}>
            <CheckCircle2 className="w-3 h-3"/> Complete · ₹{fee}
          </button>
        </>
      )}
    </div>
  );
}

const Empty = ({ msg }) => <p className="text-ink-soft text-sm py-2">{msg}</p>;

function CompleteVisitModal({ appt, defaultFee, onClose }) {
  const [fee, setFee] = useState(defaultFee || 0);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/partner/vet/appointments/${appt.appointment_id}/complete`, { amount_inr: Number(fee) || 0 });
      onClose();
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-xl font-extrabold">Complete visit</h3>
          <button onClick={onClose}><X className="w-5 h-5"/></button>
        </div>
        <div className="bg-leaf/30 rounded-2xl p-3 text-sm mb-3">
          <b>{appt.pet_name}</b> · {appt.service}
          <div className="text-xs text-ink-soft mt-0.5">Owner: {appt.owner_name || "—"}</div>
        </div>
        <form onSubmit={submit} className="space-y-3" data-testid="complete-form">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">Visit fee charged (₹)</span>
            <input type="number" min="0" value={fee} onChange={e => setFee(e.target.value)} className="mt-1 w-full border border-line rounded-xl px-3 py-2.5 bg-surface-muted/40 font-bold outline-none focus:border-brand" data-testid="fee-input"/>
          </label>
          <div className="bg-sky/10 border border-sky/30 rounded-2xl p-3 text-xs">
            <IndianRupee className="inline w-3.5 h-3.5 mr-1"/> Your earnings will be added to the weekly payout batch (15% platform fee).
          </div>
          <button disabled={saving} data-testid="confirm-complete" className="w-full bg-brand text-white rounded-full py-3 font-bold disabled:opacity-60">
            {saving ? "Saving…" : "Mark complete & log earning"}
          </button>
          <Link to={`/app/vet/prescribe/${appt.pet_id}?appt_id=${appt.appointment_id}`} className="block text-center text-xs font-bold text-brand hover:underline">Or write the prescription first →</Link>
        </form>
      </div>
    </div>
  );
}
